import { createSlice } from '@reduxjs/toolkit';

// Per-admission streaming state. Keyed by admission UUID.
// section status: 'pending' | 'streaming' | 'done' | 'error'
// admission status: 'idle' | 'connecting' | 'streaming' | 'completed' | 'failed'
//
// The slice is scoped to a single tenant (`tenantSlug`). It is cleared whenever
// the selected tenant changes (see extraReducers) and re-hydrated from that
// tenant's own localStorage keys via hydrateStreamingForTenant().

// localStorage keys are namespaced by tenant slug so one tenant's AI charts
// never leak into another tenant's session in the same browser.
export const lsUuidMapKey = (slug) => `cynthia_uuid_map:${slug}`;
export const lsStreamKey = (slug, uuid) => `cynthia_stream:${slug}:${uuid}`;

// Pre-tenant-scoping keys: browser-global, so they must never be read again.
const LEGACY_UUID_MAP_KEY = 'cynthia_uuid_map';
const LEGACY_STREAM_KEY_PREFIX = 'cynthia_stream_';

// AbortControllers for in-flight streams, keyed by admissionUuid
const streamControllers = new Map();

function lsSet(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

function lsGet(key) {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
}

// The slice stores the tenant slug that Profile normalises into selectedTenant.id.
export const tenantSlugOf = (tenant) => {
  const slug = tenant?.id ?? tenant?.slug;
  return slug == null || slug === '' ? null : String(slug);
};

// One-time cleanup of the legacy, non-namespaced keys. Safe to call repeatedly.
export function purgeLegacyStreamingKeys() {
  try {
    const doomed = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key === LEGACY_UUID_MAP_KEY || (key && key.startsWith(LEGACY_STREAM_KEY_PREFIX))) {
        doomed.push(key);
      }
    }
    doomed.forEach((key) => localStorage.removeItem(key));
  } catch {}
}

// Reads the persisted AI-chart state for one tenant only.
export function loadPersistedStreamingState(slug) {
  const admissions = {};
  if (!slug) return { admissions, admissionUuidMap: {} };
  const admissionUuidMap = lsGet(lsUuidMapKey(slug)) || {};
  for (const streamingUuid of new Set(Object.values(admissionUuidMap))) {
    const stored = lsGet(lsStreamKey(slug, streamingUuid));
    if (stored?.sections) {
      admissions[streamingUuid] = {
        status: 'completed',
        currentSection: null,
        sections: stored.sections,
        error: null,
      };
    }
  }
  return { admissions, admissionUuidMap };
}

const makeInitialState = (tenantSlug = null) => ({
  tenantSlug,
  admissions: {},
  mostRecentUuid: null,
  // Maps main-API admission UUID → streaming CyntheticAdmission UUID
  admissionUuidMap: {},
});

const streamingSlice = createSlice({
  name: 'streaming',
  initialState: makeInitialState(),
  reducers: {
    // Clears all AI-chart state and binds the slice to `tenantSlug` (or none).
    resetStreaming(state, action) {
      return makeInitialState(action?.payload?.tenantSlug ?? null);
    },

    // Merges persisted state for the active tenant; ignored if the tenant has
    // since changed. Never overwrites live (in-session) admission state.
    restorePersistedStreaming(state, action) {
      const { tenantSlug, admissions, admissionUuidMap } = action.payload;
      if (tenantSlug !== state.tenantSlug) return;
      state.admissionUuidMap = { ...admissionUuidMap, ...state.admissionUuidMap };
      for (const [uuid, adm] of Object.entries(admissions || {})) {
        if (!state.admissions[uuid]) state.admissions[uuid] = adm;
      }
    },

    initAdmissionStream(state, action) {
      const { uuid } = action.payload;
      state.mostRecentUuid = uuid;
      state.admissions[uuid] = {
        status: 'connecting',
        currentSection: null,
        sections: {},
        error: null,
      };
    },

    mapAdmissionUuid(state, action) {
      const { mainUuid, streamingUuid } = action.payload;
      if (mainUuid && streamingUuid) {
        state.admissionUuidMap[mainUuid] = streamingUuid;
      }
    },

    sectionStarted(state, action) {
      const { uuid, section, label } = action.payload;
      if (!state.admissions[uuid]) return;
      state.admissions[uuid].status = 'streaming';
      state.admissions[uuid].currentSection = section;
      state.admissions[uuid].sections[section] = {
        status: 'streaming',
        label: label || section,
        text: '',
      };
    },

    tokenReceived(state, action) {
      const { uuid, section, text } = action.payload;
      const sec = state.admissions[uuid]?.sections[section];
      if (sec) {
        sec.text += text;
      }
    },

    sectionCompleted(state, action) {
      const { uuid, section } = action.payload;
      const sec = state.admissions[uuid]?.sections[section];
      if (sec) {
        sec.status = 'done';
      }
    },

    sectionFailed(state, action) {
      const { uuid, section, message } = action.payload;
      if (!state.admissions[uuid]) return;
      const sec = state.admissions[uuid].sections[section];
      if (sec) {
        sec.status = 'error';
        sec.error = message;
      }
    },

    generationCompleted(state, action) {
      const { uuid } = action.payload;
      if (state.admissions[uuid]) {
        state.admissions[uuid].status = 'completed';
        state.admissions[uuid].currentSection = null;
      }
    },

    generationFailed(state, action) {
      const { uuid, message } = action.payload;
      if (state.admissions[uuid]) {
        state.admissions[uuid].status = 'failed';
        state.admissions[uuid].error = message;
      }
    },

    clearAdmissionStream(state, action) {
      const { uuid } = action.payload;
      delete state.admissions[uuid];
    },

    hydrateAdmission(state, action) {
      const { uuid, sections } = action.payload;
      state.admissions[uuid] = {
        status: 'completed',
        currentSection: null,
        sections,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    // Matched by type string: importing userSlice here would create an import
    // cycle (userSlice → Constants → store → streamingSlice).
    builder.addMatcher(
      (action) => action.type === 'user/selectTenant',
      (state, action) => {
        const slug = tenantSlugOf(action.payload);
        if (slug !== state.tenantSlug) return makeInitialState(slug);
        return undefined;
      },
    );
  },
});

export const {
  resetStreaming,
  restorePersistedStreaming,
  initAdmissionStream,
  mapAdmissionUuid,
  hydrateAdmission,
  sectionStarted,
  tokenReceived,
  sectionCompleted,
  sectionFailed,
  generationCompleted,
  generationFailed,
  clearAdmissionStream,
} = streamingSlice.actions;

export default streamingSlice.reducer;

const activeSlug = (getState) => getState().streaming?.tenantSlug ?? null;

// Resolves the selected tenant's API base, or null when no tenant is selected.
// Never falls back to the central API: AI charts are tenant data.
async function tenantApiBase(getState) {
  const apiBaseUrl = getState().user?.selectedTenant?.api_base_url;
  if (!apiBaseUrl) return null;
  const { default: config } = await import('../utils/Constants');
  return config.getTenantApiBase(apiBaseUrl);
}

async function authHeader() {
  const { fetchAuthSession } = await import('@aws-amplify/auth');
  const session = await fetchAuthSession();
  if (!session.tokens?.idToken) return null;
  return { Authorization: `Bearer ${session.tokens.idToken.toString()}` };
}

// Loads the given tenant's persisted AI-chart state into the slice. Called on
// tenant switch (see tenantSwitchMiddleware), after the slice has been reset.
export function hydrateStreamingForTenant(slug) {
  return (dispatch, getState) => {
    if (!slug || activeSlug(getState) !== slug) return;
    dispatch(restorePersistedStreaming({ tenantSlug: slug, ...loadPersistedStreamingState(slug) }));
  };
}

// Thunk that opens the SSE stream and dispatches slice actions
export function startAdmissionStream(admissionUuid) {
  return async (dispatch, getState) => {
    const slug = activeSlug(getState);
    if (!slug) return;
    const { streamAdmissionGeneration } = await import('../services/admissionStreamService');

    const controller = new AbortController();
    streamControllers.set(admissionUuid, controller);
    dispatch(initAdmissionStream({ uuid: admissionUuid }));

    // Drops events (and aborts the stream) once the user has switched tenant,
    // so a chart generating for tenant A never lands in tenant B's state.
    const guard = (fn) => (payload) => {
      if (activeSlug(getState) !== slug) {
        controller.abort();
        return;
      }
      fn(payload);
    };

    const streamingUrl = getState().user.selectedTenant?.streaming_url || '';
    try {
      await streamAdmissionGeneration(admissionUuid, {
        onSectionStart: guard(({ section, label }) =>
          dispatch(sectionStarted({ uuid: admissionUuid, section, label }))),

        onToken: guard(({ section, text }) =>
          dispatch(tokenReceived({ uuid: admissionUuid, section, text }))),

        onSectionEnd: guard(({ section }) =>
          dispatch(sectionCompleted({ uuid: admissionUuid, section }))),

        onSectionError: guard(({ section, message }) =>
          dispatch(sectionFailed({ uuid: admissionUuid, section, message }))),

        onDone: guard(() => {
          dispatch(generationCompleted({ uuid: admissionUuid }));
          const sections = getState().streaming.admissions[admissionUuid]?.sections;
          if (sections) lsSet(lsStreamKey(slug, admissionUuid), { sections, savedAt: Date.now() });
        }),
      }, controller.signal, streamingUrl);
    } catch (err) {
      if (err.name !== 'AbortError' && activeSlug(getState) === slug) {
        dispatch(generationFailed({ uuid: admissionUuid, message: err.message }));
      }
    } finally {
      streamControllers.delete(admissionUuid);
    }
  };
}

// Thunk that cancels an in-flight stream (no-op if already completed/absent)
export function cancelAdmissionStream(admissionUuid) {
  return () => {
    const controller = streamControllers.get(admissionUuid);
    if (controller) controller.abort();
    // Map cleanup happens in the startAdmissionStream finally block
  };
}

// Thunk that maps main→streaming UUID and persists the map immediately
export function mapAndPersistAdmissionUuid({ mainUuid, streamingUuid }) {
  return (dispatch, getState) => {
    const slug = activeSlug(getState);
    if (!slug) return;
    dispatch(mapAdmissionUuid({ mainUuid, streamingUuid }));
    lsSet(lsUuidMapKey(slug), getState().streaming.admissionUuidMap);
  };
}

// Fetches the current tenant's CyntheticAdmissions and fills admissionUuidMap
// from each item's admission_uuid. Sections are NOT hydrated eagerly; rows load
// them on demand via fetchAdmissionSections.
export function syncAdmissionUuidMap() {
  return async (dispatch, getState) => {
    const slug = activeSlug(getState);
    if (!slug) return;
    try {
      const base = await tenantApiBase(getState);
      if (!base) return;
      const headers = await authHeader();
      if (!headers) return;

      const res = await fetch(`${base}/api/v1/medchart/cynthetic-admissions`, { headers });
      if (!res.ok) return;

      const data = await res.json();
      // The tenant may have changed while the request was in flight.
      if (activeSlug(getState) !== slug) return;
      // Backend returns { items: [...] }; fall back to flat array or .results
      const items = Array.isArray(data) ? data : (data.items || data.results || []);

      const existing = getState().streaming.admissionUuidMap;
      let changed = false;
      for (const item of items) {
        if (item.uuid && item.admission_uuid && !existing[item.admission_uuid]) {
          dispatch(mapAdmissionUuid({ mainUuid: item.admission_uuid, streamingUuid: item.uuid }));
          changed = true;
        }
      }
      if (changed) lsSet(lsUuidMapKey(slug), getState().streaming.admissionUuidMap);
    } catch {}
  };
}

// Fetches completed sections from the current tenant's API and hydrates Redux.
// No-op if sections are already loaded, uuid is falsy, or no tenant is selected.
export function fetchAdmissionSections(streamingUuid) {
  return async (dispatch, getState) => {
    if (!streamingUuid) return;
    if (getState().streaming.admissions[streamingUuid]) return;
    const slug = activeSlug(getState);
    if (!slug) return;

    try {
      const base = await tenantApiBase(getState);
      if (!base) return;
      const headers = await authHeader();
      if (!headers) return;

      const res = await fetch(`${base}/api/v1/medchart/cynthetic-admissions/${streamingUuid}/sections`, { headers });
      if (!res.ok) return;

      const data = await res.json();
      if (activeSlug(getState) !== slug) return;
      if (!Array.isArray(data) || data.length === 0) return;

      // Backend returns [{ section_key, title, content }, ...]
      // Convert to the sections map shape used by the slice
      const sections = {};
      for (const s of data) {
        if (s.section_key && s.content) {
          sections[s.section_key] = {
            status: 'done',
            label: s.title || s.section_key,
            text: s.content,
          };
        }
      }

      if (Object.keys(sections).length > 0) {
        dispatch(hydrateAdmission({ uuid: streamingUuid, sections }));
      }
    } catch {}
  };
}
